/**
 * Activity-related TypeScript types
 * Shared between frontend and backend
 */
import type { ContentLang } from '../constants/bilingual.constants.js';

export enum ActivityCategory {
  NATURE = 'NATURE',
  MUSIC = 'MUSIC',
  DANCE = 'DANCE',
  SPORTS = 'SPORTS',
  ART = 'ART',
  COOKING = 'COOKING',
  STEM = 'STEM',
  OTHER = 'OTHER',
}

export enum ActivityStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum Region {
  SOUTH_BAY = 'SOUTH_BAY',
  PENINSULA = 'PENINSULA',
  EAST_BAY = 'EAST_BAY',
  SAN_FRANCISCO = 'SAN_FRANCISCO',
  OTHER = 'OTHER',
}

export interface ActivityHost {
  id: string;
  displayName: string;
  avatarUrl?: string;
  bioZh?: string | null;
  bioEn?: string | null;
  bioAutoLang?: ContentLang | null;
  joinedAt?: string;
}

export interface ActivitySession {
  id: string;
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  maxCapacity: number;
  currentBookings: number;
  spotsLeft: number;
  price?: number | null; // Session child price in cents (null = use activity default)
  adultPrice?: number | null; // Session adult price in cents (null = use activity default)
  descriptionCN?: string | null; // Chinese session description
  descriptionEN?: string | null; // English session description
  descriptionAutoLang?: ContentLang | null; // Which side is an unreviewed machine translation
}

export interface ActivityImage {
  id: string;
  imageUrl: string;
  isCover: boolean;
  order: number;
}

export interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang?: ContentLang | null;
  slug: string;
  descriptionCN: string | null;
  descriptionEN: string | null;
  descriptionAutoLang?: ContentLang | null;
  highlights?: string;
  category: ActivityCategory;
  /** @deprecated Free-text venue name. Use `region` + `locationDetails` via `formatActivityLocation`. Will be dropped in a separate migration. */
  location?: string;
  locationDetails?: string; // Detailed address; shown on public activity detail page and booking confirmation
  region: Region[]; // Bay Area regions this activity covers; required (≥1) at creation. Multi-region supported.
  city: string;
  ageMin: number;
  ageMax: number;
  price: number; // in cents (child price)
  adultPrice: number; // in cents, 0=free
  childRequired: boolean; // false = adult-only activity
  adultRequired: boolean; // always true this phase
  childCountsTowardCapacity: boolean;
  adultCountsTowardCapacity: boolean;
  maxParticipants: number;
  duration: number; // in minutes
  refundPolicy?: string;
  requirements?: string;
  status: ActivityStatus;
  rejectionReason?: string;
  host: ActivityHost;
  images?: ActivityImage[];
  sessions?: ActivitySession[];
  upcomingSessions?: ActivitySession[];
  coverImage?: string;
  rating?: number;
  reviewCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityListItem {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  descriptionCN: string | null;
  descriptionEN: string | null;
  highlights?: string;
  category: ActivityCategory;
  /** @deprecated See Activity.location. */
  location?: string;
  locationDetails?: string;
  region: Region[];
  city: string;
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice: number;
  childRequired: boolean;
  adultRequired: boolean;
  childCountsTowardCapacity: boolean;
  adultCountsTowardCapacity: boolean;
  duration: number;
  coverImage?: string;
  host: {
    id: string;
    displayName: string;
  };
  upcomingSessions: ActivitySession[];
  rating?: number;
  reviewCount?: number;
  createdAt: string;
}

export interface CreateActivityInput {
  titleCN?: string | null;
  titleEN?: string | null;
  titleAutoLang?: ContentLang | null;
  descriptionCN?: string | null;
  descriptionEN?: string | null;
  descriptionAutoLang?: ContentLang | null;
  highlights?: string;
  category: ActivityCategory;
  /** @deprecated See Activity.location. */
  location?: string;
  locationDetails?: string;
  region: Region[];
  city: string;
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice?: number;
  childRequired?: boolean;
  adultRequired?: boolean;
  childCountsTowardCapacity?: boolean;
  adultCountsTowardCapacity?: boolean;
  maxParticipants: number;
  duration: number;
  refundPolicy?: string;
  requirements?: string;
  sessions: {
    startTime: string;
    endTime: string;
    maxCapacity: number;
    price?: number | null; // Session child price in cents (null = use activity default)
    adultPrice?: number | null; // Session adult price in cents (null = use activity default)
    descriptionCN?: string | null; // Chinese session description
    descriptionEN?: string | null; // English session description
    descriptionAutoLang?: ContentLang | null;
  }[];
  imageUrls: string[];
}

export interface UpdateActivityInput extends Partial<CreateActivityInput> {
  status?: ActivityStatus;
  rejectionReason?: string;
}

export interface ActivityFilters {
  category?: ActivityCategory;
  hostId?: string;
  city?: string;
  ageMin?: number;
  ageMax?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sort?: 'createdAt' | 'price' | 'rating';
  order?: 'asc' | 'desc';
}

export interface ActivityListResponse {
  activities: ActivityListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
